/**
 * Phase 6 — Maverick Assist Mode (provider)
 *
 * Holds session-only dismissal state (keyed by `${type}:${sourceId}:${fingerprint}`)
 * and exposes the lifecycle API (`act` / `dismiss` / `defer`) that
 * `MaverickAssistPanel` calls into.
 *
 * Recommendations themselves are computed by `useMaverickAssistRecommendations`
 * from real workflow state on every render — there is no recommendation
 * "store" to drift out of sync.
 *
 * ─── DEV-ONLY VERIFICATION SURFACE ──────────────────────────────────────────
 * Two helpers exist purely to validate Phase 6 wiring without changing
 * production behavior or thresholds:
 *   • `generatedCount` / `dismissedCount` exposed for the dev debug strip.
 *   • `_devInjectSample()` / `_devClearSample()` prepend a clearly-labeled
 *     [DEMO] recommendation to the visible list. They early-return outside
 *     `import.meta.env.DEV` and the sample never comes from the engine.
 * The whole verification surface tree-shakes out in production builds.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useOptionalAppData } from "@/data/AppDataContext";
import { useActionQueueStore } from "@/data/actionQueue/context";
import { runAction } from "@/lib/actionRunner";
import {
  generateRecommendations,
  recommendationDismissalKey,
} from "./assistEngine";
import type { MaverickRecommendation } from "./assistTypes";

interface MaverickAssistContextValue {
  /** Visible recommendations (cap-applied, dismissals filtered, dev sample prepended). */
  visible: MaverickRecommendation[];
  /** Total generated count, before cap and dismissal filtering. */
  totalCount: number;
  /** Engine-generated count (excludes any dev sample). Same as totalCount. */
  generatedCount: number;
  /** Number of currently-active dismissals (session-only). */
  dismissedCount: number;
  /** True when a dev sample is currently injected. */
  hasDevSample: boolean;
  /** Run the recommendation's action via runAction(). */
  act: (rec: MaverickRecommendation) => void;
  /** Hide for the current session, keyed to current state fingerprint. */
  dismiss: (rec: MaverickRecommendation) => void;
  /** Same as dismiss in Phase 6 — explicit alias for forward-compat. */
  defer: (rec: MaverickRecommendation) => void;

  /**
   * @internal DEV-ONLY. Inject a clearly-labeled [DEMO] recommendation so the
   * panel can be visually verified. No-op outside `import.meta.env.DEV`.
   */
  _devInjectSample: () => void;
  /** @internal DEV-ONLY. Remove the injected [DEMO] recommendation. */
  _devClearSample: () => void;
}

const MaverickAssistContext = createContext<MaverickAssistContextValue | undefined>(undefined);

const VISIBLE_CAP = 3;

/* ─── DEV-ONLY: sample recommendation factory ─────────────────────────────── */

const DEV_SAMPLE_ID = "__dev_sample__";

function buildDevSampleRecommendation(): MaverickRecommendation {
  return {
    id: DEV_SAMPLE_ID,
    type: "stalled_queue_item",
    title: "[DEMO] Sample recommendation",
    summary: "Dev-only — confirms the panel can render content.",
    reason:
      "This is an injected sample for visual verification of Maverick Assist. It is not produced by the engine and never appears in production builds.",
    recommendedAction: "No-op (demo)",
    actionKey: "__dev_demo__",
    action: { label: "No-op (demo)", executionMode: "direct", handler: () => {} },
    priority: "medium",
    confidence: "low",
    sourceType: "queue_item",
    sourceId: DEV_SAMPLE_ID,
    isSafeToRun: false,
    stateFingerprint: "demo",
  };
}

export function MaverickAssistProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const appData = useOptionalAppData();
  const followUpTasks = appData?.followUpTasks ?? [];
  const { items: queueItems } = useActionQueueStore();

  // Session-only dismissal — Set, not array, for O(1) checks. No persistence.
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(() => new Set());

  // Dev-only sample state. Always defined (cheap), but only ever set true via
  // _devInjectSample which itself is gated by import.meta.env.DEV.
  const [hasDevSample, setHasDevSample] = useState(false);

  const recommendations = useMemo(
    () =>
      generateRecommendations({
        handoffs: followUpTasks,
        queueItems,
        // AI signals lane intentionally not wired here yet — duplicate detector
        // still works against handoff + queue, which is the high-signal path.
        aiSignals: [],
        navigate,
      }).all,
    [followUpTasks, queueItems, navigate],
  );

  const visible = useMemo(() => {
    const filtered = recommendations.filter(
      (r) => !dismissedKeys.has(recommendationDismissalKey(r)),
    );
    const capped = filtered.slice(0, VISIBLE_CAP);
    // Dev sample is prepended AFTER capping so it never displaces real recs
    // beyond what the cap allows. Production builds short-circuit on hasDevSample.
    if (import.meta.env.DEV && hasDevSample) {
      return [buildDevSampleRecommendation(), ...capped].slice(0, VISIBLE_CAP + 1);
    }
    return capped;
  }, [recommendations, dismissedKeys, hasDevSample]);

  const act = useCallback(
    (rec: MaverickRecommendation) => {
      // Maverick NEVER mutates state directly. Always route through runAction(),
      // identical to a manual operator click.
      void runAction(rec.action, { navigate });
    },
    [navigate],
  );

  const dismiss = useCallback((rec: MaverickRecommendation) => {
    // Dev sample is dismissed by clearing its flag, not by polluting the
    // dismissedKeys set with a fake fingerprint.
    if (rec.id === DEV_SAMPLE_ID) {
      setHasDevSample(false);
      return;
    }
    setDismissedKeys((prev) => {
      const next = new Set(prev);
      next.add(recommendationDismissalKey(rec));
      return next;
    });
  }, []);

  const _devInjectSample = useCallback(() => {
    if (!import.meta.env.DEV) return; // hard gate, tree-shakes in prod
    setHasDevSample(true);
  }, []);

  const _devClearSample = useCallback(() => {
    if (!import.meta.env.DEV) return;
    setHasDevSample(false);
  }, []);

  const value = useMemo<MaverickAssistContextValue>(
    () => ({
      visible,
      totalCount: recommendations.length,
      generatedCount: recommendations.length,
      dismissedCount: dismissedKeys.size,
      hasDevSample,
      act,
      dismiss,
      defer: dismiss, // Phase 6: same behavior; explicit alias for future divergence.
      _devInjectSample,
      _devClearSample,
    }),
    [visible, recommendations.length, dismissedKeys.size, hasDevSample, act, dismiss, _devInjectSample, _devClearSample],
  );

  return (
    <MaverickAssistContext.Provider value={value}>
      {children}
    </MaverickAssistContext.Provider>
  );
}

export function useMaverickAssist(): MaverickAssistContextValue {
  const ctx = useContext(MaverickAssistContext);
  if (!ctx) {
    // Soft fallback — Maverick Intelligence panel renders outside this provider
    // in edge cases (e.g. portal routes). Return an empty, no-op shape so
    // consumers can render a clean nothing instead of crashing.
    return {
      visible: [],
      totalCount: 0,
      generatedCount: 0,
      dismissedCount: 0,
      hasDevSample: false,
      act: () => {},
      dismiss: () => {},
      defer: () => {},
      _devInjectSample: () => {},
      _devClearSample: () => {},
    };
  }
  return ctx;
}
